import { TAG_LABELS } from './tag-labels';
/**
 * List-view facet filtering.
 *
 * A "facet" is a single filterable tag, encoded as `type:value`
 * (e.g. `family:closedguard`, `concept:kuzushi`, `tag:leglock-entry`).
 * The list filter holds a set of facets plus an AND/OR mode; a row passes
 * when it satisfies ALL (AND) or ANY (OR) of the selected facets.
 *
 * The same encoding lets a chip anywhere (Drawer detail, ListView row)
 * become a one-click filter: it just hands its `type:value` to
 * `filterListByTag`.
 */
import type { Counter, Move, Position } from '../types/domain';

export type Facet = string;

/** Entity fields a facet can key off. Order also drives dropdown grouping. */
export const FACET_TYPES = [
  'family',
  'concept',
  'tag',
  'category',
  'sub',
  'gi',
  'diff',
  'zone',
  'posture',
  'dom',
] as const;
export type FacetType = (typeof FACET_TYPES)[number];

export function makeFacet(type: FacetType, value: string): Facet {
  return `${type}:${value}`;
}

export function splitFacet(f: Facet): { type: string; value: string } {
  const i = f.indexOf(':');
  return i < 0 ? { type: '', value: f } : { type: f.slice(0, i), value: f.slice(i + 1) };
}

/**
 * 'closed-guard' → 'Closed Guard'; short tokens upper-cased.
 *
 * The advantage values are the exception. Left to the generic rule,
 * `tori` and `uke` would come out as "TORI" and "UKE" — judo terms,
 * shouted. They get their plain-English labels instead, from the same
 * table the drawer uses, so a facet chip and a node tag never disagree
 * about what to call the same thing.
 */
export function prettyValue(v: string): string {
  const advantage = TAG_LABELS['#' + v];
  if (advantage) return advantage;
  return v
    .split(/[-_]/)
    .map((w) => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

type AnyEntity = Partial<
  Position & Move & Counter & { submissionType?: string; dominance?: string }
>;

/** Does an entity carry a given facet value? */
export function entityHasFacet(e: AnyEntity, facet: Facet): boolean {
  const { type, value } = splitFacet(facet);
  switch (type) {
    case 'family':
      return e.family === value;
    case 'concept':
      return (e.concepts ?? []).includes(value);
    case 'tag':
      return (e.tags ?? []).includes(value);
    case 'category':
      return e.category === value;
    case 'sub':
      return (e as { submissionType?: string }).submissionType === value;
    case 'gi':
      return e.gi === value;
    case 'diff':
      return e.difficulty === value;
    case 'zone':
      return e.zone === value;
    case 'posture':
      return e.posture === value;
    case 'dom':
      return (e as { dominance?: string }).dominance === value;
    default:
      return false;
  }
}

/** Row passes the facet set under the given mode. */
export function matchesFacets(
  e: AnyEntity,
  facets: Facet[],
  mode: 'all' | 'any',
): boolean {
  if (facets.length === 0) return true;
  return mode === 'all'
    ? facets.every((f) => entityHasFacet(e, f))
    : facets.some((f) => entityHasFacet(e, f));
}

/**
 * Build the distinct facet universe from a set of entities, each with a
 * count of how many entities carry it (for ranking the dropdown). Only
 * facets present in the passed set are returned, so the autocomplete never
 * offers a tag that would match nothing in the current list mode.
 */
export function buildFacetUniverse(entities: AnyEntity[]): Map<Facet, number> {
  const counts = new Map<Facet, number>();
  const bump = (f: Facet) => counts.set(f, (counts.get(f) ?? 0) + 1);
  for (const e of entities) {
    if (e.family) bump(makeFacet('family', e.family));
    for (const c of e.concepts ?? []) bump(makeFacet('concept', c));
    for (const t of e.tags ?? []) bump(makeFacet('tag', t));
    if (e.category) bump(makeFacet('category', e.category));
    const sub = (e as { submissionType?: string }).submissionType;
    if (sub) bump(makeFacet('sub', sub));
    if (e.gi) bump(makeFacet('gi', e.gi));
    if (e.difficulty) bump(makeFacet('diff', e.difficulty));
    if (e.zone) bump(makeFacet('zone', e.zone));
    if (e.posture) bump(makeFacet('posture', e.posture));
    const dom = (e as { dominance?: string }).dominance;
    if (dom) bump(makeFacet('dom', dom));
  }
  return counts;
}
